/**
 * Drag/handle manipulation of EXISTING design-tab objects — extracted from
 * SketchObjectTool so the Design tab's selection tool offers the same UX
 * (same two-layer shape as the flip/transform gizmo interaction wrappers):
 *
 *  - RUN RESIZE HANDLES on the single selected LINE run: start/end double
 *    arrows (wall runs reflow offset+width; free runs like stairs move the
 *    grabbed anchor endpoint); beams get endpoint spheres + a perpendicular
 *    middle arrow instead.
 *  - BODY DRAGS: FREE/WALL object move (placement pipeline with alignment
 *    guides), whole-beam translation (endpoints re-sticking to walls), and
 *    the opening-sketch slide along its wall (live hole re-cut).
 *
 * Every commit pushes an undo snapshot; a moved drag swallows its trailing
 * click. The host tool forwards its mouse choreography and stays in charge
 * of WHAT is grabbed (its own picking) and of selection semantics.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { DragEventData } from '../../../BaseTool';
import { Snapshot, ToolUndoRedoManager } from '../../ToolUndoRedoManager';
import { PixelHitTester } from '../selection/PixelHitTester';
import { PointoramaWallSketch } from '../../../../utils/PointoramaWallSketch';
import { PointoramaSketchObject } from '../../../../utils/PointoramaSketchObject';
import { RoundHandleMarker } from '../../../../utils/RoundHandleMarker';
import { SketchObjectAnchorKind, type SketchObjectFrame } from '../../../../sketchObjects/types';
import { DIMENSION_OFFSET_PARAM, placedDimensionOffset } from '../../../../sketchObjects/custom/customSketchObjectType';
import { squareDimensionRun } from './dimensionSquare';
import { at } from '../../../../sketchObjects/glyphs/frame';
import { getNumberParamProperty } from '../../../../sketchObjects/paramSchema';
import type { RegisteredSketchObjectType } from '../../../../sketchObjects/registry';
import { getConstraintStrategy } from '../../../../sketchObjects/constraints/constraintRegistry';
import { resolveFreePlacementAt } from '../../../../sketchObjects/constraints/FreeConstraint';
import { capturePlacementValues } from '../../../../sketchObjects/constraints/placementCapture';
import type { AlignGuide } from '../../../../sketchObjects/constraints/alignSnap';
import type { PlacementContext, PlacementValues } from '../../../../sketchObjects/constraints/types';
import { extendedRunSpan, unclampedOffsetAt, wallEdgeGeometry, type WallEdgeGeometry } from '../../../../sketchObjects/constraints/WallConstraint';
import { closestPointOnEdge, pointAtArcLength, lineArcIntersections } from '../../../../utils/skeleton/arcGeometry';
import { projectMouseToFloor, snapToWallEdge, SKETCH_OBJECT_WALL_SNAP, type WallEdgeHit } from '../../implementations/WallSketchSnapEngine';
import {
  clampOpeningOffsetToClearSpan,
  openingFootprint,
  pickOpeningSketchAtPixel,
  cursorOffsetOnOpeningEdge,
  createOpeningOffsetMoveSnapshot,
  WallHoleRebuildScheduler,
  OpeningFootprintHighlight,
} from '../openingSketch/OpeningSketchShared';
import {
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchWallEdgeIdProperty,
  getSketchObjectAnchorProperty,
  getSketchObjectRotationProperty,
  getSketchObjectWidthProperty,
  getSketchObjectDepthProperty,
  getSketchObjectWallOffsetProperty,
  getSketchObjectWallEdgeIdProperty,
  getWallSketchEdgesProperty,
  getWallSketchNodesProperty,
} from '@pointorama/potree-shared';

type Coordinate = { x: number; y: number; z: number };

// ─── Shared constants (handles + beam snapping) ────────────────────────

export const HANDLE_PICK_RADIUS_PX = 26;
/** World cap on the handles as a fraction of the run length (zoomed far out). */
export const HANDLE_MAX_ARROW_RUN_FRAC = 0.4;
const HANDLE_RENDER_ORDER = 1000;
/** Solid gizmos (spheres) can't use the per-pixel invert shader — pick their
 *  color from the active background instead: white on the dark Plan
 *  background, black on the light one. */
const GIZMO_COLOR_ON_DARK = new THREE.Color(0xffffff);
export function gizmoContrastColor(viewer: Viewer, base: THREE.Color): THREE.Color {
  const background = viewer.getBackground();
  return background === 'black' || background === 'dark' ? GIZMO_COLOR_ON_DARK : base;
}

/** Shortest committable run (m). */
export const MIN_RUN_LENGTH = 0.1;
/** Reach (m) of the beam endpoint wall smart-connect. */
export const BEAM_WALL_SNAP_REACH = 0.5;
/** Angular tolerance of the beam 90° direction snap. */
export const BEAM_ANGLE_SNAP_TOL = (10 * Math.PI) / 180;

// ─── Shared snapping helpers (also used by the sketch tool's draw flows) ─

/** Whether the event suppresses the beam's snapping (wall smart-connect and
 *  the 90° direction snap) — Shift or Ctrl held = place/drag exactly at the
 *  cursor. */
export function snapOverridden(event: MouseEvent): boolean {
  return event.shiftKey || event.ctrlKey;
}

/**
 * Direction-snap the segment start→end to the nearest 90° step of
 * `referenceDir` within {@link BEAM_ANGLE_SNAP_TOL}; NULL when the segment is
 * too far off any step (no snap). The segment length is preserved.
 */
export function snapSegmentTo90(start: THREE.Vector3, end: THREE.Vector3, referenceDir: { x: number; y: number }): THREE.Vector3 | null {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy);
  const refLen = Math.hypot(referenceDir.x, referenceDir.y);
  if (len < 1e-9 || refLen < 1e-9) return null;
  const step = Math.PI / 2;
  const delta = Math.atan2(dy, dx) - Math.atan2(referenceDir.y, referenceDir.x);
  const snappedDelta = Math.round(delta / step) * step;
  // Compare on the wrapped difference so e.g. −179° vs 181° reads as 2°.
  let diff = delta - snappedDelta;
  while (diff > Math.PI) diff -= 2 * Math.PI;
  while (diff < -Math.PI) diff += 2 * Math.PI;
  if (Math.abs(diff) > BEAM_ANGLE_SNAP_TOL) return null;
  const angle = Math.atan2(referenceDir.y, referenceDir.x) + snappedDelta;
  return new THREE.Vector3(start.x + Math.cos(angle) * len, start.y + Math.sin(angle) * len, end.z);
}

/** A snapped free-run endpoint: on a wall (`edgeId` when it is a sketch edge) or free, with the wall direction there. */
export interface FreeRunPointInfo {
  point: THREE.Vector3;
  onWall: boolean;
  edgeId: string | null;
  edgeDir: { x: number; y: number } | null;
  /** Snapped onto a wall-skin corner (a dimension end); `edgeDir` is then one of its sides. */
  corner?: boolean;
  /** Snapped onto a wall-skin face (not a corner): its two ends, which a dimension can measure in one click. */
  face?: { a: THREE.Vector3; b: THREE.Vector3 };
}

/** Endpoint of a free run: snapped onto a nearby wall centerline (smart
 *  connect, unless overridden), else the raw floor point under the cursor. */
export function snapFreeRunPointInfo(viewer: Viewer, hitTester: PixelHitTester, skeleton: PointoramaWallSketch | null, floorZ: number, event: MouseEvent): FreeRunPointInfo | null {
  const hit = snapOverridden(event) || !skeleton ? null : snapToWallEdge(event, skeleton, hitTester, viewer.scene.getActiveCamera(), viewer.renderer.domElement, SKETCH_OBJECT_WALL_SNAP);
  if (hit && skeleton) {
    const geom = wallEdgeGeometry(skeleton, hit.edgeId);
    let edgeDir: { x: number; y: number } | null = null;
    if (geom) {
      const { tangent } = pointAtArcLength(geom.a, geom.b, geom.bulge, hit.offset);
      edgeDir = { x: tangent.x, y: tangent.y };
    }
    return { point: hit.point.clone(), onWall: true, edgeId: hit.edgeId, edgeDir };
  }
  const floor = projectMouseToFloor(event, viewer.renderer.domElement, viewer.scene.getActiveCamera(), floorZ);
  return floor ? { point: new THREE.Vector3(floor.x, floor.y, floorZ), onWall: false, edgeId: null, edgeDir: null } : null;
}

/** Screen reach of a dimension endpoint's corner and face snaps (px). */
const DIMENSION_CORNER_SNAP_PX = 12;
const DIMENSION_FACE_SNAP_PX = 10;

type SkinPoint = { x: number; y: number };

/** Skin rings per sketch, recomputed only when its nodes / edges change (pointer moves are frequent). */
const skinRingCache = new WeakMap<PointoramaWallSketch, { nodes: unknown; edges: unknown; rings: SkinPoint[][] }>();

function wallSkinRings(sketch: PointoramaWallSketch): SkinPoint[][] {
  const nodes = getWallSketchNodesProperty(sketch.identifier).value;
  const edges = getWallSketchEdgesProperty(sketch.identifier).value;
  const cached = skinRingCache.get(sketch);
  if (cached && cached.nodes === nodes && cached.edges === edges) return cached.rings;
  const rings = sketch.getWallRegionRings();
  skinRingCache.set(sketch, { nodes, edges, rings });
  return rings;
}

function unitDirection(a: SkinPoint, b: SkinPoint): SkinPoint | null {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  return len > 1e-9 ? { x: (b.x - a.x) / len, y: (b.y - a.y) / len } : null;
}

/**
 * Endpoint of a DIMENSION: a dimension measures what is drawn, so its ends
 * snap to the wall SKIN, a corner first, else the nearest point on a wall
 * face, else the floor point under the cursor (Shift / Ctrl: always the
 * cursor). `edgeDir` is the face direction (the 90° snap reference); `edgeId`
 * stays null (a skin face is not a sketch edge).
 */
export function snapDimensionPointInfo(viewer: Viewer, hitTester: PixelHitTester, skeleton: PointoramaWallSketch | null, floorZ: number, event: MouseEvent): FreeRunPointInfo | null {
  const floor = projectMouseToFloor(event, viewer.renderer.domElement, viewer.scene.getActiveCamera(), floorZ);
  if (!floor) return null;
  const free: FreeRunPointInfo = { point: new THREE.Vector3(floor.x, floor.y, floorZ), onWall: false, edgeId: null, edgeDir: null };
  if (!skeleton || snapOverridden(event)) return free;

  const mouse = hitTester.mousePx(event);
  const pixelDistance = (p: SkinPoint): number => hitTester.projectWorldToPixel({ x: p.x, y: p.y, z: floorZ })?.distanceTo(mouse) ?? Infinity;
  const rings = wallSkinRings(skeleton);
  const snapped = (p: SkinPoint, dir: SkinPoint | null): FreeRunPointInfo => ({ point: new THREE.Vector3(p.x, p.y, floorZ), onWall: true, edgeId: null, edgeDir: dir });

  let corner: FreeRunPointInfo | null = null;
  let cornerDistance = DIMENSION_CORNER_SNAP_PX;
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const distance = pixelDistance(ring[i]);
      if (distance >= cornerDistance) continue;
      cornerDistance = distance;
      corner = { ...snapped(ring[i], unitDirection(ring[i], ring[(i + 1) % ring.length])), corner: true };
    }
  }
  if (corner) return corner;

  let face: FreeRunPointInfo | null = null;
  let faceDistance = DIMENSION_FACE_SNAP_PX;
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      const foot = closestPointOnEdge(floor, a, b, 0).point;
      const distance = pixelDistance(foot);
      if (distance >= faceDistance) continue;
      faceDistance = distance;
      face = { ...snapped(foot, unitDirection(a, b)), face: { a: new THREE.Vector3(a.x, a.y, floorZ), b: new THREE.Vector3(b.x, b.y, floorZ) } };
    }
  }
  return face ?? free;
}

/** How close (m) a dimension end must be to the wall skin to count as sitting on it. */
const ON_SKIN_REACH = 0.01;

/**
 * The wall-skin directions at `p` — a corner's two sides, a face's one, none
 * off the walls: what a dimension's origin squares to (see squareDimensionRun).
 */
export function skinSidesAt(skeleton: PointoramaWallSketch | null, p: SkinPoint): SkinPoint[] {
  if (!skeleton) return [];
  const rings = wallSkinRings(skeleton);
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      if (Math.hypot(ring[i].x - p.x, ring[i].y - p.y) > ON_SKIN_REACH) continue;
      const next = unitDirection(ring[i], ring[(i + 1) % ring.length]);
      const previous = unitDirection(ring[i], ring[(i + ring.length - 1) % ring.length]);
      return [next, previous].filter((side): side is SkinPoint => side !== null);
    }
  }
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      if (closestPointOnEdge(p, a, b, 0).distance > ON_SKIN_REACH) continue;
      const side = unitDirection(a, b);
      return side ? [side] : [];
    }
  }
  return [];
}

/** Nearest wall centerline foot within {@link BEAM_WALL_SNAP_REACH} of `p`,
 *  with the wall's local tangent there; null when no wall is in reach. */
export function nearestWallFoot(skeleton: PointoramaWallSketch | null, p: { x: number; y: number }): { point: { x: number; y: number }; dir: { x: number; y: number } } | null {
  if (!skeleton) return null;
  let best: { point: { x: number; y: number }; dir: { x: number; y: number } } | null = null;
  let bestDist = BEAM_WALL_SNAP_REACH;
  for (const edge of skeleton.getEdges()) {
    const a = skeleton.getNode(edge.startNodeId);
    const b = skeleton.getNode(edge.endNodeId);
    if (!a || !b) continue;
    const ap = { x: a.position.x, y: a.position.y };
    const bp = { x: b.position.x, y: b.position.y };
    const bulge = edge.bulge ?? 0;
    const foot = closestPointOnEdge(p, ap, bp, bulge);
    if (foot.distance < bestDist) {
      bestDist = foot.distance;
      const { tangent } = pointAtArcLength(ap, bp, bulge, foot.s);
      best = { point: { x: foot.point.x, y: foot.point.y }, dir: { x: tangent.x, y: tangent.y } };
    }
  }
  return best;
}

/** Snap a point onto the nearest wall centerline within reach — keeps a
 *  beam endpoint gliding ALONG its wall while the beam moves across. */
export function snapPointToWall(skeleton: PointoramaWallSketch | null, p: { x: number; y: number }): { x: number; y: number } {
  return nearestWallFoot(skeleton, p)?.point ?? p;
}

/**
 * Intersection of the ray start→through with an edge's centerline (straight
 * chord or arc), picked nearest to `nearTo` and capped to the shift the
 * angle correction can plausibly cause — near-parallel geometry must not
 * teleport the endpoint along the wall. Null when nothing qualifies.
 */
export function intersectRayWithEdge(skeleton: PointoramaWallSketch | null, start: THREE.Vector3, through: THREE.Vector3, edgeId: string, nearTo: THREE.Vector3): THREE.Vector3 | null {
  const geom = skeleton ? wallEdgeGeometry(skeleton, edgeId) : null;
  if (!geom) return null;
  const dx = through.x - start.x;
  const dy = through.y - start.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return null;
  const hits = lineArcIntersections({ x: start.x, y: start.y }, { x: dx / len, y: dy / len }, geom.a, geom.b, geom.bulge);
  const maxShift = Math.max(BEAM_WALL_SNAP_REACH, len * Math.tan(BEAM_ANGLE_SNAP_TOL) * 1.5);
  let best: { x: number; y: number } | null = null;
  let bestDist = maxShift;
  for (const p of hits) {
    const d = Math.hypot(p.x - nearTo.x, p.y - nearTo.y);
    if (d <= bestDist) {
      bestDist = d;
      best = p;
    }
  }
  return best ? new THREE.Vector3(best.x, best.y, nearTo.z) : null;
}

/** Cursor's along-wall offset on the run's edge, clamped to the wall's
 *  usable span. On straight edges the span extends across COLINEAR
 *  continuations (T-junction nodes), so resize can cross them; on curved
 *  edges it's the edge's own arc length. */
export function cursorOffsetOnEdge(viewer: Viewer, skeleton: PointoramaWallSketch | null, event: MouseEvent, floorZ: number, geom: WallEdgeGeometry): number | null {
  const floor = projectMouseToFloor(event, viewer.renderer.domElement, viewer.scene.getActiveCamera(), floorZ);
  if (!floor) return null;
  if (Math.abs(geom.bulge) < 1e-6) {
    const dx = geom.b.x - geom.a.x;
    const dy = geom.b.y - geom.a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return null;
    const s = ((floor.x - geom.a.x) * dx + (floor.y - geom.a.y) * dy) / len;
    const span = skeleton ? extendedRunSpan(skeleton, geom.edgeId) : { lo: 0, hi: geom.len };
    return Math.max(span.lo, Math.min(span.hi, s));
  }
  const foot = closestPointOnEdge({ x: floor.x, y: floor.y }, geom.a, geom.b, geom.bulge);
  return Math.max(0, Math.min(geom.len, foot.s));
}

/** Undo snapshot for an object move: toggles the placement values
 *  before/after (also used by the sketch tool's rotate/arrow-key moves). */
export function createPlacementMoveSnapshot(objectId: string, type: RegisteredSketchObjectType, before: PlacementValues, after: PlacementValues): Snapshot {
  let applyBefore = true;
  const snapshot: Snapshot = {
    restore: (): Snapshot => {
      try {
        getConstraintStrategy(type.constraint.kind).writePlacement(objectId, applyBefore ? before : after);
      } catch {
        /* property service not ready */
      }
      applyBefore = !applyBefore;
      return snapshot;
    },
  };
  return snapshot;
}

// ─── Controller ────────────────────────────────────────────────────────

export interface SelectedObjectDragDeps {
  viewer: Viewer;
  hitTester: PixelHitTester;
  getWallSketch: () => PointoramaWallSketch | null;
  getFloorZ: () => number;
  undoRedo: () => ToolUndoRedoManager | null;
  /** Overlay group the handle markers live in (host-owned lifecycle). */
  ensureOverlay: () => THREE.Group;
  /** Alignment guides during a FREE body drag (optional visual). */
  onGuides?: (guides: AlignGuide[], z: number) => void;
}

/** 'start' | 'end' | 'middle' name a fixed handle; 'offset' either handle on
 *  a dimension's line (both drag its offset); `{ depth }` indexes the custom
 *  depth handles of a free run (see SketchObjectDepthHandleSpec). */
export type ResizeHandleEnd = 'start' | 'end' | 'middle' | 'offset' | { depth: number };

/** Where a dimension's offset handles sit along its line (fractions of the length from the middle). */
const DIMENSION_OFFSET_HANDLE_AT = [-0.25, 0.25] as const;

export class SelectedObjectDragController {
  /** Object whose handles are currently shown (pick target). */
  private handleObject: PointoramaSketchObject | null = null;
  private startHandle: RoundHandleMarker | null = null;
  private endHandle: RoundHandleMarker | null = null;
  private beamStartMarker: RoundHandleMarker | null = null;
  private beamEndMarker: RoundHandleMarker | null = null;
  private beamMiddleHandle: RoundHandleMarker | null = null;
  private startHandlePos: THREE.Vector3 | null = null;
  private endHandlePos: THREE.Vector3 | null = null;
  private middleHandlePos: THREE.Vector3 | null = null;
  // Extra DEPTH handles of the selected free run, from the type's custom
  // resize-handle layout (multi-flight stairs put them on the stair's other
  // ends). Markers are pooled; specs carry the live position + pulled side.
  private depthHandles: RoundHandleMarker[] = [];
  private depthHandleSpecs: Array<{ pos: THREE.Vector3; side: 1 | -1; apply?: (objectId: string, pulledDepth: number) => number }> = [];
  // A selected dimension's two offset handles, on its (offset) line: dragging
  // either slides the line toward / away from the measured points.
  private offsetHandles: RoundHandleMarker[] = [];
  private offsetHandlePositions: THREE.Vector3[] = [];

  /** Drag state for resizing a run by one of its handles — WALL runs
   *  (cabinets) reflow offset+width along their edge; FREE runs (stairs)
   *  move the grabbed anchor endpoint; beams move endpoints/body with wall
   *  re-sticking. */
  private resizeDrag:
    | { kind: 'wall'; object: PointoramaSketchObject; end: 'start' | 'end'; edgeId: string; beforeOffset: number; beforeWidth: number; moved: boolean }
    | { kind: 'free'; object: PointoramaSketchObject; end: 'start' | 'end'; beforeAnchor: Coordinate[]; moved: boolean }
    | {
        kind: 'depth';
        object: PointoramaSketchObject;
        side: 1 | -1;
        /** Custom drag semantics of the grabbed handle (see SketchObjectDepthHandleSpec.apply). */
        apply?: (objectId: string, pulledDepth: number) => number;
        beforeAnchor: Coordinate[];
        beforeDepth: number;
        /** Numeric param values at drag start — an `apply` drag may rewrite them, undo restores them. */
        beforeParams: Record<string, number>;
        moved: boolean;
      }
    | {
        kind: 'dimensionOffset';
        object: PointoramaSketchObject;
        typeId: string;
        beforeOffset: number;
        moved: boolean;
      }
    | {
        kind: 'beam';
        object: PointoramaSketchObject;
        end: 'start' | 'end' | 'middle' | 'body';
        beforeAnchor: Coordinate[];
        /** Floor point grabbed on mousedown — middle/body drags measure their delta from here. */
        grab: { x: number; y: number };
        moved: boolean;
      }
    | null = null;

  /** Body drag of a whole (non-beam) object — placement-pipeline move. */
  private objectDrag: {
    object: PointoramaSketchObject;
    type: RegisteredSketchObjectType;
    before: PlacementValues;
    /** Frame-center minus grab point: keeps the grab offset under the cursor. */
    grabOffset: { x: number; y: number } | null;
    sticky: { edgeId: string | null };
    moved: boolean;
  } | null = null;

  /** Slide of an opening sketch along its wall. */
  private openingSlide: { openingId: string; edgeId: string; grabDelta: number; beforeOffset: number; moved: boolean } | null = null;

  private holeRebuild: WallHoleRebuildScheduler;
  private slideHighlight: OpeningFootprintHighlight;
  /** True while this controller set `selectionDisabled` for a handle press. */
  private suppressedSelection = false;

  constructor(private deps: SelectedObjectDragDeps) {
    this.holeRebuild = new WallHoleRebuildScheduler(() => this.deps.getWallSketch());
    this.slideHighlight = new OpeningFootprintHighlight(deps.viewer, () => this.deps.ensureOverlay());
  }

  // ─── Handle display (call per frame with the object to decorate) ─────

  /** Show/refresh the resize handles for `object` (LINE-anchored runs only —
   *  anything else hides them). Positions follow the object and camera. */
  refreshHandles(object: PointoramaSketchObject | null): void {
    const type = object?.getType();
    const frame = object && type && type.anchor === SketchObjectAnchorKind.LINE ? object.resolveFrame() : null;
    if (!object || !type || !frame) {
      this.hideHandles();
      return;
    }

    this.handleObject = object;
    const overlay = this.deps.ensureOverlay();
    const hw = frame.width / 2;
    this.startHandlePos = new THREE.Vector3(frame.center.x - frame.dir.x * hw, frame.center.y - frame.dir.y * hw, frame.z);
    this.endHandlePos = new THREE.Vector3(frame.center.x + frame.dir.x * hw, frame.center.y + frame.dir.y * hw, frame.z);

    // Beams replace the run-resize handles with their own UX: endpoint
    // handles + a perpendicular middle one (move-across handle).
    if (type.drawLikeWall) {
      if (this.startHandle) this.startHandle.visible = false;
      if (this.endHandle) this.endHandle.visible = false;
      if (!this.beamStartMarker || !this.beamEndMarker || !this.beamMiddleHandle) {
        this.beamStartMarker = this.createHandle(overlay);
        this.beamEndMarker = this.createHandle(overlay);
        this.beamMiddleHandle = this.createHandle(overlay);
      }
      // Dimensions keep only their endpoints (the gizmo moves the whole one).
      const middle = !type.dimension;
      this.middleHandlePos = middle ? new THREE.Vector3(frame.center.x, frame.center.y, frame.z) : null;
      const along = new THREE.Vector3(frame.dir.x, frame.dir.y, 0);
      this.beamStartMarker.position.copy(this.startHandlePos);
      this.beamEndMarker.position.copy(this.endHandlePos);
      this.beamStartMarker.setDirection(along);
      this.beamEndMarker.setDirection(along);
      if (this.middleHandlePos) this.beamMiddleHandle.position.copy(this.middleHandlePos);
      this.beamMiddleHandle.setDirection(new THREE.Vector3(frame.norm.x, frame.norm.y, 0));
      for (const marker of [this.beamStartMarker, this.beamEndMarker, this.beamMiddleHandle]) marker.setMaxWorldLength(frame.width * HANDLE_MAX_ARROW_RUN_FRAC);
      this.beamStartMarker.visible = true;
      this.beamEndMarker.visible = true;
      this.beamMiddleHandle.visible = middle;
      this.refreshOffsetHandles(object, type, frame, overlay);
      // Size NOW so the first rendered frame isn't at a stale/huge scale.
      this.beamStartMarker.updateScale();
      this.beamEndMarker.updateScale();
      this.beamMiddleHandle.updateScale();
      this.hideDepthHandles();
      return;
    }

    this.middleHandlePos = null;
    this.hideBeamHandles();
    if (!this.startHandle || !this.endHandle) {
      this.startHandle = this.createHandle(overlay);
      this.endHandle = this.createHandle(overlay);
    }

    // FREE runs may declare a custom handle layout (multi-flight stairs put
    // the run-end handles on the entry flight and add depth handles on the
    // stair's other ends). Depth drags rewrite the anchor, so the layout is
    // honored for anchor-carrying FREE runs only.
    const layout = type.constraint.kind === 'FREE' ? type.getResizeHandleLayout(frame, object.identifier) : null;
    if (layout?.start) {
      const p = at(frame, layout.start[0], layout.start[1]);
      this.startHandlePos.set(p.x, p.y, frame.z);
    }
    if (layout?.end) {
      const p = at(frame, layout.end[0], layout.end[1]);
      this.endHandlePos.set(p.x, p.y, frame.z);
    }

    const dir = new THREE.Vector3(frame.dir.x, frame.dir.y, 0);
    for (const [handle, pos] of [
      [this.startHandle, this.startHandlePos],
      [this.endHandle, this.endHandlePos],
    ] as const) {
      handle.position.copy(pos);
      handle.setDirection(dir);
      handle.setMaxWorldLength(frame.width * HANDLE_MAX_ARROW_RUN_FRAC);
      handle.visible = true;
      // Size NOW so the first rendered frame isn't at a stale/huge scale.
      handle.updateScale();
    }

    // Depth handles — pooled markers, arrows along the frame normal.
    const depthSpecs = layout?.depth ?? [];
    this.depthHandleSpecs = depthSpecs.map((spec) => {
      const p = at(frame, spec.u, spec.n);
      return { pos: new THREE.Vector3(p.x, p.y, frame.z), side: spec.side, apply: spec.apply };
    });
    while (this.depthHandles.length < depthSpecs.length) this.depthHandles.push(this.createHandle(overlay));
    const norm = new THREE.Vector3(frame.norm.x, frame.norm.y, 0);
    this.depthHandles.forEach((handle, i) => {
      const spec = this.depthHandleSpecs[i];
      if (!spec) {
        handle.visible = false;
        return;
      }
      handle.position.copy(spec.pos);
      handle.setDirection(norm);
      handle.setMaxWorldLength(frame.depth * HANDLE_MAX_ARROW_RUN_FRAC);
      handle.visible = true;
      // Size NOW so the first rendered frame isn't at a stale/huge scale.
      handle.updateScale();
    });
  }

  private hideDepthHandles(): void {
    this.depthHandleSpecs = [];
    for (const handle of this.depthHandles) handle.visible = false;
  }

  /** A new (hidden until placed) handle in the overlay — the Object Editor's Test-mode handle look. */
  private createHandle(overlay: THREE.Object3D): RoundHandleMarker {
    const handle = new RoundHandleMarker(this.deps.viewer);
    handle.setRenderOrder(HANDLE_RENDER_ORDER);
    overlay.add(handle);
    return handle;
  }

  private hideBeamHandles(): void {
    if (this.beamStartMarker) this.beamStartMarker.visible = false;
    if (this.beamEndMarker) this.beamEndMarker.visible = false;
    if (this.beamMiddleHandle) this.beamMiddleHandle.visible = false;
    this.hideOffsetHandles();
  }

  private hideOffsetHandles(): void {
    this.offsetHandlePositions = [];
    for (const handle of this.offsetHandles) handle.visible = false;
  }

  /** A dimension's offset handles: on its line, pointing across it (other runs: none). */
  private refreshOffsetHandles(object: PointoramaSketchObject, type: RegisteredSketchObjectType, frame: SketchObjectFrame, overlay: THREE.Object3D): void {
    if (!type.dimension) {
      this.hideOffsetHandles();
      return;
    }
    const offset = placedDimensionOffset(type, object.identifier);
    this.offsetHandlePositions = DIMENSION_OFFSET_HANDLE_AT.map(
      (at) => new THREE.Vector3(frame.center.x + frame.dir.x * frame.width * at + frame.norm.x * offset, frame.center.y + frame.dir.y * frame.width * at + frame.norm.y * offset, frame.z),
    );
    this.offsetHandlePositions.forEach((pos, i) => {
      let handle = this.offsetHandles[i];
      if (!handle) {
        handle = this.createHandle(overlay);
        this.offsetHandles[i] = handle;
      }
      handle.position.copy(pos);
      handle.setDirection(new THREE.Vector3(frame.norm.x, frame.norm.y, 0));
      handle.setMaxWorldLength(frame.width * HANDLE_MAX_ARROW_RUN_FRAC);
      handle.visible = true;
      handle.updateScale();
    });
  }

  hideHandles(): void {
    this.handleObject = null;
    this.startHandlePos = null;
    this.endHandlePos = null;
    this.middleHandlePos = null;
    if (this.startHandle) this.startHandle.visible = false;
    if (this.endHandle) this.endHandle.visible = false;
    this.hideBeamHandles();
    this.hideDepthHandles();
  }

  /** The run handle under a pixel cursor, if any ('middle' only for beams). */
  pickHandleAtPixel(mousePx: THREE.Vector2): ResizeHandleEnd | null {
    if (!this.handleObject) return null;
    const candidates: Array<readonly [ResizeHandleEnd, THREE.Vector3 | null]> = [
      ['start', this.startHandlePos],
      ['end', this.endHandlePos],
      ['middle', this.middleHandlePos],
      ...this.offsetHandlePositions.map((pos) => ['offset', pos] as const),
      ...this.depthHandleSpecs.map((spec, i) => [{ depth: i }, spec.pos] as const),
    ];
    let best: ResizeHandleEnd | null = null;
    let bestDist = HANDLE_PICK_RADIUS_PX;
    for (const [end, pos] of candidates) {
      if (!pos) continue;
      const px = this.deps.hitTester.projectWorldToPixel(pos);
      if (!px) continue;
      const d = px.distanceTo(mousePx);
      if (d < bestDist) {
        bestDist = d;
        best = end;
      }
    }
    return best;
  }

  // ─── Arming (mousedown) ──────────────────────────────────────────────

  /**
   * Arm a resize/beam-handle drag when the mousedown hits one of the shown
   * handles. True = armed (the caller must not run its other grabs). Also
   * suppresses the parallel click-selection — the handles sit off the glyph,
   * so the click's normal pick would land on empty space and DESELECT.
   */
  armResizeHandle(event: MouseEvent): boolean {
    const mousePx = this.deps.hitTester.mousePx(event);
    const handleEnd = this.pickHandleAtPixel(mousePx);
    if (!handleEnd || !this.handleObject) return false;
    const handleObject = this.handleObject;
    const type = handleObject.getType();
    const id = handleObject.identifier;
    if (handleEnd === 'offset') {
      // Dimension offset handle: the drag slides the line across.
      if (!type?.dimension) return false;
      this.clearArmed();
      this.resizeDrag = { kind: 'dimensionOffset', object: handleObject, typeId: type.id, beforeOffset: placedDimensionOffset(type, id), moved: false };
      this.suppressSelection();
      return true;
    }
    if (typeof handleEnd === 'object') {
      // Depth handle (custom layout of a FREE run): the drag moves the pulled
      // norm-side edge, the opposite edge stays pinned.
      const spec = this.depthHandleSpecs[handleEnd.depth];
      const anchor = getSketchObjectAnchorProperty(id).value;
      if (spec && type && anchor.length >= 2) {
        this.clearArmed();
        this.resizeDrag = {
          kind: 'depth',
          object: handleObject,
          side: spec.side,
          apply: spec.apply,
          beforeAnchor: anchor.map((p) => ({ ...p })),
          beforeDepth: getSketchObjectDepthProperty(id).value ?? 0,
          beforeParams: SelectedObjectDragController.numericParamValues(type, id),
          moved: false,
        };
        this.suppressSelection();
        return true;
      }
      return false;
    }
    if (type?.drawLikeWall) {
      // Beam: endpoint handles move that endpoint (wall-snapped); the
      // middle handle shifts the whole beam perpendicular to its axis.
      const anchor = getSketchObjectAnchorProperty(id).value;
      const grab = projectMouseToFloor(event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), anchor[0]?.z ?? this.deps.getFloorZ());
      if (anchor.length >= 2 && grab) {
        this.clearArmed();
        this.resizeDrag = {
          kind: 'beam',
          object: handleObject,
          end: handleEnd,
          beforeAnchor: anchor.map((p) => ({ ...p })),
          grab: { x: grab.x, y: grab.y },
          moved: false,
        };
        this.suppressSelection();
        return true;
      }
    } else if (handleEnd !== 'middle' && type && type.anchor === SketchObjectAnchorKind.LINE && type.constraint.kind === 'WALL') {
      const edgeId = getSketchObjectWallEdgeIdProperty(id).value || '';
      if (edgeId) {
        this.clearArmed();
        this.resizeDrag = {
          kind: 'wall',
          object: handleObject,
          end: handleEnd,
          edgeId,
          beforeOffset: getSketchObjectWallOffsetProperty(id).value ?? 0,
          beforeWidth: getSketchObjectWidthProperty(id).value ?? 0,
          moved: false,
        };
        this.suppressSelection();
        return true;
      }
    } else if (handleEnd !== 'middle' && type && type.anchor === SketchObjectAnchorKind.LINE) {
      // Free run (stair): the handle drags one anchor endpoint along the run.
      const anchor = getSketchObjectAnchorProperty(id).value;
      if (anchor.length >= 2) {
        this.clearArmed();
        this.resizeDrag = {
          kind: 'free',
          object: handleObject,
          end: handleEnd,
          beforeAnchor: anchor.map((p) => ({ ...p })),
          moved: false,
        };
        this.suppressSelection();
        return true;
      }
    }
    return false;
  }

  /** Arm a whole-beam BODY translation from a grab at `event` (both endpoints
   *  re-sticking to nearby walls while dragging; Shift/Ctrl moves it raw). */
  armBeamBodyDrag(object: PointoramaSketchObject, event: MouseEvent): void {
    const anchor = getSketchObjectAnchorProperty(object.identifier).value;
    const grab = projectMouseToFloor(event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), anchor[0]?.z ?? this.deps.getFloorZ());
    if (anchor.length < 2 || !grab) return;
    this.clearArmed();
    this.resizeDrag = {
      kind: 'beam',
      object,
      end: 'body',
      beforeAnchor: anchor.map((p) => ({ ...p })),
      grab: { x: grab.x, y: grab.y },
      moved: false,
    };
  }

  /** Arm a body drag of a (non-beam) object — moved through the placement
   *  pipeline so it wall-aligns/collides like a fresh placement. */
  armObjectBodyDrag(object: PointoramaSketchObject, type: RegisteredSketchObjectType, event: MouseEvent): void {
    const grabFrame = object.resolveFrame();
    const grabFloor = projectMouseToFloor(event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), this.deps.getFloorZ());
    this.clearArmed();
    this.objectDrag = {
      object,
      type,
      before: capturePlacementValues(object.identifier),
      grabOffset: grabFrame && grabFloor ? { x: grabFrame.center.x - grabFloor.x, y: grabFrame.center.y - grabFloor.y } : null,
      sticky: { edgeId: null },
      moved: false,
    };
  }

  /** Arm a SLIDE of the opening sketch under the cursor along its wall (no
   *  prior selection required). True = an opening was grabbed. */
  armOpeningSlide(event: MouseEvent): boolean {
    const skeleton = this.deps.getWallSketch();
    if (!skeleton) return false;
    const openingId = pickOpeningSketchAtPixel(this.deps.viewer, skeleton, this.deps.hitTester.mousePx(event), this.deps.hitTester);
    if (!openingId) return false;
    try {
      const edgeId = getOpeningSketchWallEdgeIdProperty(openingId).value || '';
      const beforeOffset = getOpeningSketchOffsetProperty(openingId).value ?? 0;
      const cursor = edgeId ? cursorOffsetOnOpeningEdge(event, this.deps.viewer, skeleton, edgeId) : null;
      if (edgeId && cursor !== null) {
        this.clearArmed();
        this.openingSlide = { openingId, edgeId, grabDelta: cursor - beforeOffset, beforeOffset, moved: false };
        return true;
      }
    } catch {
      /* property service not ready */
    }
    return false;
  }

  // ─── State queries ───────────────────────────────────────────────────

  /** A press armed one of the drags (host blocks orbit on this). */
  isArmed(): boolean {
    return this.resizeDrag !== null || this.objectDrag !== null || this.openingSlide !== null;
  }

  /** True mid opening-slide — the slide owns the footprint highlight then. */
  openingSlideMoved(): boolean {
    return this.openingSlide?.moved ?? false;
  }

  /** True on the trailing click of a MOVED drag — the host's click logic
   *  (select/place/deselect) must not run. */
  consumedClick(): boolean {
    return (this.objectDrag?.moved || this.resizeDrag?.moved || this.openingSlide?.moved) ?? false;
  }

  /** A plain click landed on a handle (sub-threshold press): restore the
   *  selection suppression set on mousedown. True = consumed. */
  handleClick(event: MouseEvent): boolean {
    if (this.consumedClick()) return true;
    if (this.pickHandleAtPixel(this.deps.hitTester.mousePx(event))) {
      this.restoreSelection();
      return true;
    }
    return false;
  }

  // ─── Drag application ────────────────────────────────────────────────

  /** Apply the armed drag for this frame. True = consumed. */
  handleDrag(drag: DragEventData): boolean {
    if (this.resizeDrag) {
      this.applyResizeDrag(drag);
      return true;
    }
    if (this.openingSlide) {
      this.applyOpeningSlide(drag);
      return true;
    }
    if (this.objectDrag) {
      this.applyObjectDrag(drag);
      return true;
    }
    return false;
  }

  private applyResizeDrag(drag: DragEventData): void {
    const skeleton = this.deps.getWallSketch();
    if (!this.resizeDrag) return;
    if (this.resizeDrag.kind === 'dimensionOffset') {
      // The line follows the cursor across (snapped to wall corners / faces, so it can line up with a wall).
      const dragState = this.resizeDrag;
      const frame = dragState.object.resolveFrame();
      const info = frame ? snapDimensionPointInfo(this.deps.viewer, this.deps.hitTester, skeleton, frame.z, drag.event) : null;
      if (!frame || !info) return;
      const offset = (info.point.x - frame.center.x) * frame.norm.x + (info.point.y - frame.center.y) * frame.norm.y;
      getNumberParamProperty(dragState.typeId, DIMENSION_OFFSET_PARAM, dragState.object.identifier).value = offset;
      dragState.moved = true;
      return;
    }
    if (this.resizeDrag.kind === 'beam') {
      const dragState = this.resizeDrag;
      const [a0, a1] = dragState.beforeAnchor;
      // Dimensions run the same drags, but measure the wall skin: endpoints
      // snap to its corners / faces and a moved dimension keeps its ends.
      const dimension = dragState.object.getType()?.dimension ?? false;
      // Shift/Ctrl suppress the wall/90° snaps for every beam drag.
      const overridden = snapOverridden(drag.event) || (dimension && dragState.end !== 'start' && dragState.end !== 'end');
      if (dragState.end === 'middle' || dragState.end === 'body') {
        // MIDDLE handle: shift the whole beam PERPENDICULAR to its axis.
        // BODY grab: translate the whole beam freely with the cursor.
        // Either way each endpoint then re-sticks to a nearby wall
        // centerline (unless overridden), so the ends slide ALONG their
        // supporting walls.
        const floor = projectMouseToFloor(drag.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), a0.z);
        if (!floor) return;
        const len = Math.hypot(a1.x - a0.x, a1.y - a0.y);
        if (len < 1e-6) return;
        let dx = floor.x - dragState.grab.x;
        let dy = floor.y - dragState.grab.y;
        if (dragState.end === 'middle') {
          const perpX = -(a1.y - a0.y) / len;
          const perpY = (a1.x - a0.x) / len;
          const d = dx * perpX + dy * perpY;
          dx = perpX * d;
          dy = perpY * d;
        }
        const movedA = overridden ? { x: a0.x + dx, y: a0.y + dy } : snapPointToWall(skeleton, { x: a0.x + dx, y: a0.y + dy });
        const movedB = overridden ? { x: a1.x + dx, y: a1.y + dy } : snapPointToWall(skeleton, { x: a1.x + dx, y: a1.y + dy });
        if (Math.hypot(movedB.x - movedA.x, movedB.y - movedA.y) < MIN_RUN_LENGTH) return;
        this.writeBeamAnchor(dragState.object.identifier, [
          { ...movedA, z: a0.z },
          { ...movedB, z: a1.z },
        ]);
      } else {
        // Move one endpoint (wall-snapped); the other end stays pinned. The
        // 90° direction snap references the WALLS at the endpoints — the
        // wall supporting the PINNED end first, else the wall under the
        // cursor — never the beam's original axis, which would only pull
        // the beam back toward its old direction. With no wall at either
        // end there is no angle snap.
        const snapEndpoint = dimension ? snapDimensionPointInfo : snapFreeRunPointInfo;
        const info = snapEndpoint(this.deps.viewer, this.deps.hitTester, skeleton, this.deps.getFloorZ(), drag.event);
        if (!info) return;
        const pinned = dragState.end === 'start' ? a1 : a0;
        const pinned3 = new THREE.Vector3(pinned.x, pinned.y, info.point.z);
        const referenceDir = nearestWallFoot(skeleton, pinned)?.dir ?? info.edgeDir;
        const angleSnapped = overridden || !referenceDir ? null : snapSegmentTo90(pinned3, info.point, referenceDir);
        let snap = info.point;
        if (dimension) {
          // A dimension squares to the wall at its pinned (origin) end, which stays put.
          if (!overridden) snap = squareDimensionRun(pinned3, skinSidesAt(skeleton, pinned), info, false).end;
        } else if (info.onWall) {
          // DOUBLE SNAP: endpoint on a wall + direction near a 90° step of
          // an endpoint wall → land ON the wall at the snapped ray.
          if (angleSnapped && info.edgeId) {
            snap = intersectRayWithEdge(skeleton, pinned3, angleSnapped, info.edgeId, info.point) ?? info.point;
          }
        } else if (angleSnapped) {
          snap = angleSnapped;
        }
        const anchor: Coordinate[] = dragState.end === 'start' ? [{ x: snap.x, y: snap.y, z: a0.z }, { ...a1 }] : [{ ...a0 }, { x: snap.x, y: snap.y, z: a1.z }];
        if (Math.hypot(anchor[1].x - anchor[0].x, anchor[1].y - anchor[0].y) < MIN_RUN_LENGTH) return;
        this.writeBeamAnchor(dragState.object.identifier, anchor);
      }
      dragState.moved = true;
      return;
    }

    if (this.resizeDrag.kind === 'wall') {
      const geom = skeleton ? wallEdgeGeometry(skeleton, this.resizeDrag.edgeId) : null;
      if (!skeleton || !geom) return;
      const cursor = cursorOffsetOnEdge(this.deps.viewer, skeleton, drag.event, skeleton.getFloorHeight(), geom);
      if (cursor === null) return;
      const id = this.resizeDrag.object.identifier;
      const lo = getSketchObjectWallOffsetProperty(id).value ?? 0;
      const width = getSketchObjectWidthProperty(id).value ?? 0;
      const hi = lo + width;
      // Bounds follow the wall's usable span, which extends across colinear
      // continuations — runs resize straight over T-junction nodes.
      const span = extendedRunSpan(skeleton, this.resizeDrag.edgeId);
      if (this.resizeDrag.end === 'start') {
        const newLo = Math.max(span.lo, Math.min(cursor, hi - MIN_RUN_LENGTH));
        getSketchObjectWallOffsetProperty(id).value = newLo;
        getSketchObjectWidthProperty(id).value = hi - newLo;
      } else {
        const newHi = Math.min(span.hi, Math.max(cursor, lo + MIN_RUN_LENGTH));
        getSketchObjectWidthProperty(id).value = newHi - lo;
      }
      this.resizeDrag.moved = true;
      return;
    }

    if (this.resizeDrag.kind === 'depth') {
      // Depth handle: the pulled norm-side edge follows the cursor, the
      // opposite edge stays pinned. The LINE frame is centered on the
      // anchor, so the anchor shifts by half the depth delta to keep the
      // pinned edge in place; syncParams (step ↔ count) reflows live via
      // the property subscriptions.
      const dragState = this.resizeDrag;
      const [d0, d1] = dragState.beforeAnchor;
      const depthRunLength = Math.hypot(d1.x - d0.x, d1.y - d0.y);
      if (depthRunLength < 1e-6) return;
      const normX = -(d1.y - d0.y) / depthRunLength;
      const normY = (d1.x - d0.x) / depthRunLength;
      const depthFloor = projectMouseToFloor(drag.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), d0.z);
      if (!depthFloor) return;
      const centerX = (d0.x + d1.x) / 2;
      const centerY = (d0.y + d1.y) / 2;
      // Cursor's distance from the run centerline toward the pulled side;
      // the pinned edge sits beforeDepth/2 on the other side of it. A
      // custom `apply` maps that pulled distance onto the type's params
      // and answers with the resulting footprint depth.
      const toward = ((depthFloor.x - centerX) * normX + (depthFloor.y - centerY) * normY) * dragState.side;
      const pulled = Math.max(MIN_RUN_LENGTH, toward + dragState.beforeDepth / 2);
      const newDepth = Math.max(MIN_RUN_LENGTH, dragState.apply ? dragState.apply(dragState.object.identifier, pulled) : pulled);
      const shift = ((newDepth - dragState.beforeDepth) / 2) * dragState.side;
      const depthId = dragState.object.identifier;
      getSketchObjectAnchorProperty(depthId).value = dragState.beforeAnchor.map((p) => ({ x: p.x + normX * shift, y: p.y + normY * shift, z: p.z }));
      getSketchObjectDepthProperty(depthId).value = newDepth;
      dragState.moved = true;
      return;
    }

    const [a0, a1] = this.resizeDrag.beforeAnchor;
    const runLength = Math.hypot(a1.x - a0.x, a1.y - a0.y);
    if (runLength < 1e-6) return;
    const axisX = (a1.x - a0.x) / runLength;
    const axisY = (a1.y - a0.y) / runLength;
    const floor = projectMouseToFloor(drag.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), a0.z);
    if (!floor) return;
    // Cursor's signed distance along the run axis from the original start.
    const s = (floor.x - a0.x) * axisX + (floor.y - a0.y) * axisY;
    const id = this.resizeDrag.object.identifier;
    let anchor: Coordinate[];
    if (this.resizeDrag.end === 'end') {
      const len = Math.max(MIN_RUN_LENGTH, s);
      anchor = [{ ...a0 }, { x: a0.x + axisX * len, y: a0.y + axisY * len, z: a1.z }];
    } else {
      const lo = Math.min(runLength - MIN_RUN_LENGTH, s);
      anchor = [{ x: a0.x + axisX * lo, y: a0.y + axisY * lo, z: a0.z }, { ...a1 }];
    }
    getSketchObjectAnchorProperty(id).value = anchor;
    // Keep the width property in sync (the LINE frame derives width from the
    // anchor, but the property drives the properties panel / exports).
    getSketchObjectWidthProperty(id).value = Math.hypot(anchor[1].x - anchor[0].x, anchor[1].y - anchor[0].y);
    this.resizeDrag.moved = true;
  }

  private applyOpeningSlide(drag: DragEventData): void {
    const skeleton = this.deps.getWallSketch();
    if (!skeleton || !this.openingSlide) return;
    const slide = this.openingSlide;
    const cursor = cursorOffsetOnOpeningEdge(drag.event, this.deps.viewer, skeleton, slide.edgeId);
    if (cursor === null) return;
    let length = 0;
    try {
      length = getOpeningSketchLengthProperty(slide.openingId).value ?? 0;
    } catch {
      return;
    }
    const clamped = clampOpeningOffsetToClearSpan(skeleton, slide.edgeId, length, cursor - slide.grabDelta);
    try {
      getOpeningSketchOffsetProperty(slide.openingId).value = clamped;
    } catch {
      return;
    }
    slide.moved = true;
    const footprint = openingFootprint(skeleton, slide.openingId);
    if (footprint) this.slideHighlight.show([footprint.pts]);
    // The offset write only patches the glyph — re-cut the wall hole live
    // (rAF-throttled).
    this.holeRebuild.schedule();
  }

  private applyObjectDrag(drag: DragEventData): void {
    if (!this.objectDrag) return;
    const { object, type } = this.objectDrag;
    // FREE body drags keep the GRAB OFFSET: the candidate center is the
    // cursor plus the offset captured at mousedown (grabbing a corner must
    // not snap the center under the cursor), resolved through the same
    // collision/assist pipeline the placement flows use. WALL objects keep
    // the cursor-driven slide — their attachment remaps onto whatever wall
    // the cursor is over, so a fixed offset has no stable meaning there.
    if (type.constraint.kind === 'FREE' && this.objectDrag.grabOffset) {
      const floor = projectMouseToFloor(drag.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), this.deps.getFloorZ());
      if (!floor) return;
      const id = object.identifier;
      const width = getSketchObjectWidthProperty(id).value ?? type.defaultSize.width;
      const depth = getSketchObjectDepthProperty(id).value ?? type.defaultSize.depth;
      const rotation = getSketchObjectRotationProperty(id).value ?? 0;
      const point = { x: floor.x + this.objectDrag.grabOffset.x, y: floor.y + this.objectDrag.grabOffset.y };
      const placement = resolveFreePlacementAt(this.deps.viewer, this.deps.hitTester, this.deps.getWallSketch(), this.deps.getFloorZ(), id, type, point, rotation, width, depth, this.objectDrag.sticky, 'full');
      if (!placement) return;
      getConstraintStrategy('FREE').writePlacement(id, placement);
      this.deps.onGuides?.(placement.guides ?? [], placement.frame.z);
      this.objectDrag.moved = true;
      return;
    }
    const strategy = getConstraintStrategy(type.constraint.kind);
    const placement = strategy.resolveDrag(this.placementContext(drag.event), object, type);
    if (placement) {
      strategy.writePlacement(object.identifier, placement);
      this.deps.onGuides?.(placement.guides ?? [], placement.frame.z);
      this.objectDrag.moved = true;
      return;
    }
    // WALL objects with NO wall in the cursor's reach: keep sliding along
    // the CURRENT wall (cursor projected onto its edge line, clamped to the
    // span) — the drag must never dead-stop just because the mouse strayed
    // off the wall band. Same slide the gizmo's arrow handle applies.
    if (type.constraint.kind !== 'WALL') return;
    const skeleton = this.deps.getWallSketch();
    const edgeId = getSketchObjectWallEdgeIdProperty(object.identifier).value || '';
    const geom = skeleton ? wallEdgeGeometry(skeleton, edgeId) : null;
    if (!skeleton || !geom) return;
    const floor = projectMouseToFloor(drag.event, this.deps.viewer.renderer.domElement, this.deps.viewer.scene.getActiveCamera(), skeleton.getFloorHeight());
    if (!floor) return;
    const width = getSketchObjectWidthProperty(object.identifier).value ?? type.defaultSize.width;
    const cursorS = unclampedOffsetAt(geom, floor) ?? closestPointOnEdge({ x: floor.x, y: floor.y }, geom.a, geom.b, geom.bulge).s;
    try {
      getSketchObjectWallOffsetProperty(object.identifier).value = THREE.MathUtils.clamp(cursorS - width / 2, 0, Math.max(0, geom.len - width));
    } catch {
      return;
    }
    this.objectDrag.moved = true;
  }

  private placementContext(event: MouseEvent): PlacementContext {
    return {
      viewer: this.deps.viewer,
      hitTester: this.deps.hitTester,
      event,
      wallSketch: this.deps.getWallSketch(),
      floorZ: this.deps.getFloorZ(),
    };
  }

  // ─── Commit (drag end) ───────────────────────────────────────────────

  /** Commit the armed drag: undo snapshot + final rebuilds. The armed state
   *  lingers until the trailing click is swallowed (consumedClick), then
   *  clears as a fallback. True = consumed. */
  handleDragEnd(): boolean {
    if (this.resizeDrag) {
      this.restoreSelection();
      const dragState = this.resizeDrag;
      if (dragState.moved) {
        const id = dragState.object.identifier;
        if (dragState.kind === 'wall') {
          const afterOffset = getSketchObjectWallOffsetProperty(id).value ?? dragState.beforeOffset;
          const afterWidth = getSketchObjectWidthProperty(id).value ?? dragState.beforeWidth;
          if (Math.abs(afterOffset - dragState.beforeOffset) > 1e-6 || Math.abs(afterWidth - dragState.beforeWidth) > 1e-6) {
            this.deps.undoRedo()?.pushState(this.createResizeSnapshot(id, { offset: dragState.beforeOffset, width: dragState.beforeWidth }, { offset: afterOffset, width: afterWidth }));
          }
        } else if (dragState.kind === 'beam') {
          const after = getSketchObjectAnchorProperty(id).value.map((p) => ({ ...p }));
          const changed = after.length >= 2 && dragState.beforeAnchor.some((p, i) => Math.abs(p.x - after[i].x) > 1e-6 || Math.abs(p.y - after[i].y) > 1e-6);
          if (changed) {
            this.deps.undoRedo()?.pushState(this.createBeamDragSnapshot(id, dragState.beforeAnchor, after));
            // Every beam drag can relocate the run — re-file into the room
            // folder under the new position (handled in usePotreeEvents).
            this.deps.viewer.scene.dispatchEvent({ type: 'sketch_object_moved', value: { identifier: id } });
          }
        } else if (dragState.kind === 'dimensionOffset') {
          const property = getNumberParamProperty(dragState.typeId, DIMENSION_OFFSET_PARAM, id);
          const afterOffset = property.value;
          if (Math.abs(afterOffset - dragState.beforeOffset) > 1e-6) {
            const { beforeOffset, typeId } = dragState;
            let applyBefore = true;
            const snapshot: Snapshot = {
              restore: (): Snapshot => {
                try {
                  getNumberParamProperty(typeId, DIMENSION_OFFSET_PARAM, id).value = applyBefore ? beforeOffset : afterOffset;
                } catch {
                  /* property service not ready */
                }
                applyBefore = !applyBefore;
                return snapshot;
              },
            };
            this.deps.undoRedo()?.pushState(snapshot);
          }
        } else if (dragState.kind === 'depth') {
          const type = dragState.object.getType();
          const afterAnchor = getSketchObjectAnchorProperty(id).value.map((p) => ({ ...p }));
          const afterDepth = getSketchObjectDepthProperty(id).value ?? dragState.beforeDepth;
          const afterParams = type ? SelectedObjectDragController.numericParamValues(type, id) : {};
          const paramsChanged = Object.entries(dragState.beforeParams).some(([key, value]) => Math.abs((afterParams[key] ?? value) - value) > 1e-6);
          if (type && (Math.abs(afterDepth - dragState.beforeDepth) > 1e-6 || paramsChanged)) {
            this.deps.undoRedo()?.pushState(
              this.createDepthResizeSnapshot(
                id,
                type.id,
                { anchor: dragState.beforeAnchor, depth: dragState.beforeDepth, params: dragState.beforeParams },
                { anchor: afterAnchor, depth: afterDepth, params: afterParams },
              ),
            );
          }
        } else {
          const after = getSketchObjectAnchorProperty(id).value.map((p) => ({ ...p }));
          const changed = after.length >= 2 && dragState.beforeAnchor.some((p, i) => Math.abs(p.x - after[i].x) > 1e-6 || Math.abs(p.y - after[i].y) > 1e-6);
          if (changed) {
            this.deps.undoRedo()?.pushState(this.createFreeResizeSnapshot(id, dragState.beforeAnchor, after));
          }
        }
        // Keep resizeDrag until the trailing click is swallowed.
        setTimeout(() => {
          this.resizeDrag = null;
        }, 0);
      } else {
        this.resizeDrag = null;
      }
      return true;
    }

    if (this.openingSlide) {
      this.holeRebuild.cancel();
      this.slideHighlight.hide();
      const slide = this.openingSlide;
      if (slide.moved) {
        let afterOffset = slide.beforeOffset;
        try {
          afterOffset = getOpeningSketchOffsetProperty(slide.openingId).value ?? slide.beforeOffset;
        } catch {
          /* property service not ready */
        }
        if (Math.abs(afterOffset - slide.beforeOffset) > 1e-6) {
          this.deps.undoRedo()?.pushState(createOpeningOffsetMoveSnapshot(this.deps.getWallSketch, slide.openingId, slide.beforeOffset, afterOffset));
          this.deps.getWallSketch()?.updateSkinningVisualization();
          // Re-file into the adjacent room's folder (handled in usePotreeEvents).
          this.deps.viewer.scene.dispatchEvent({ type: 'opening_sketch_moved', value: { identifier: slide.openingId } });
        }
        setTimeout(() => {
          this.openingSlide = null;
        }, 0);
      } else {
        this.openingSlide = null;
      }
      return true;
    }

    if (this.objectDrag) {
      const dragState = this.objectDrag;
      if (!dragState.moved) {
        this.objectDrag = null;
        return true;
      }
      this.deps.onGuides?.([], 0);
      const after = capturePlacementValues(dragState.object.identifier);
      this.deps.undoRedo()?.pushState(createPlacementMoveSnapshot(dragState.object.identifier, dragState.type, dragState.before, after));
      // Re-file into the room folder under the new position (handled in
      // usePotreeEvents) — a viewport move is what clears a manual panel override.
      this.deps.viewer.scene.dispatchEvent({ type: 'sketch_object_moved', value: { identifier: dragState.object.identifier } });
      setTimeout(() => {
        this.objectDrag = null;
      }, 0);
      return true;
    }
    return false;
  }

  // ─── Cleanup ─────────────────────────────────────────────────────────

  /** Drop every armed state without committing (tool disable / mode switch). */
  cancel(): void {
    this.restoreSelection();
    this.resizeDrag = null;
    this.objectDrag = null;
    this.openingSlide = null;
    this.holeRebuild.cancel();
    this.slideHighlight.hide();
    this.deps.onGuides?.([], 0);
  }

  /** Dispose the handle markers (host overlay teardown). */
  dispose(): void {
    this.cancel();
    const overlay = this.deps.ensureOverlay();
    for (const handle of [this.startHandle, this.endHandle, this.beamMiddleHandle, this.beamStartMarker, this.beamEndMarker, ...this.depthHandles, ...this.offsetHandles]) {
      if (!handle) continue;
      overlay.remove(handle);
      handle.dispose();
    }
    this.depthHandles = [];
    this.offsetHandles = [];
    this.startHandle = null;
    this.endHandle = null;
    this.beamStartMarker = null;
    this.beamEndMarker = null;
    this.beamMiddleHandle = null;
    this.slideHighlight.dispose();
    this.hideHandles();
  }

  // ─── Internals ───────────────────────────────────────────────────────

  private clearArmed(): void {
    this.resizeDrag = null;
    this.objectDrag = null;
    this.openingSlide = null;
  }

  private suppressSelection(): void {
    this.deps.viewer.inputHandler.selectionDisabled = true;
    this.suppressedSelection = true;
  }

  private restoreSelection(): void {
    if (!this.suppressedSelection) return;
    this.suppressedSelection = false;
    this.deps.viewer.inputHandler.selectionDisabled = false;
  }

  /** Persist a beam's two-point anchor plus the derived rotation and width. */
  private writeBeamAnchor(id: string, anchor: Coordinate[]): void {
    getSketchObjectAnchorProperty(id).value = anchor;
    getSketchObjectRotationProperty(id).value = Math.atan2(anchor[1].y - anchor[0].y, anchor[1].x - anchor[0].x);
    getSketchObjectWidthProperty(id).value = Math.hypot(anchor[1].x - anchor[0].x, anchor[1].y - anchor[0].y);
  }

  /** Numeric param values of a placed object — the undo baseline of a depth
   *  drag with custom semantics (an `apply` may rewrite numeric params). */
  private static numericParamValues(type: RegisteredSketchObjectType, objectId: string): Record<string, number> {
    const values: Record<string, number> = {};
    for (const [key, value] of Object.entries(type.getParamValues(objectId))) {
      if (typeof value === 'number') values[key] = value;
    }
    return values;
  }

  /** Undo snapshot for a DEPTH-handle resize: toggles the anchor, the depth
   *  property and the type's numeric params together (a depth drag shifts
   *  anchor + depth — with custom `apply` semantics it rewrites params too,
   *  e.g. the U stair's leg lengths). */
  private createDepthResizeSnapshot(
    objectId: string,
    typeId: string,
    before: { anchor: Coordinate[]; depth: number; params: Record<string, number> },
    after: { anchor: Coordinate[]; depth: number; params: Record<string, number> },
  ): Snapshot {
    let applyBefore = true;
    const snapshot: Snapshot = {
      restore: (): Snapshot => {
        try {
          const values = applyBefore ? before : after;
          getSketchObjectAnchorProperty(objectId).value = values.anchor.map((p) => ({ ...p }));
          getSketchObjectDepthProperty(objectId).value = values.depth;
          for (const [key, value] of Object.entries(values.params)) {
            const property = getNumberParamProperty(typeId, key, objectId);
            if (property.value !== value) property.value = value;
          }
        } catch {
          /* property service not ready */
        }
        applyBefore = !applyBefore;
        return snapshot;
      },
    };
    return snapshot;
  }

  /** Undo snapshot for a WALL-run resize: toggles offset+width before/after. */
  private createResizeSnapshot(objectId: string, before: { offset: number; width: number }, after: { offset: number; width: number }): Snapshot {
    let applyBefore = true;
    const snapshot: Snapshot = {
      restore: (): Snapshot => {
        try {
          const values = applyBefore ? before : after;
          getSketchObjectWallOffsetProperty(objectId).value = values.offset;
          getSketchObjectWidthProperty(objectId).value = values.width;
        } catch {
          /* property service not ready */
        }
        applyBefore = !applyBefore;
        return snapshot;
      },
    };
    return snapshot;
  }

  /** Undo snapshot for a beam handle drag: toggles the anchor before/after,
   *  re-deriving rotation + width from it (a beam drag can change both). */
  private createBeamDragSnapshot(objectId: string, before: Coordinate[], after: Coordinate[]): Snapshot {
    let applyBefore = true;
    const snapshot: Snapshot = {
      restore: (): Snapshot => {
        try {
          const anchor = applyBefore ? before : after;
          this.writeBeamAnchor(objectId, anchor.map((p) => ({ ...p })));
        } catch {
          /* property service not ready */
        }
        applyBefore = !applyBefore;
        return snapshot;
      },
    };
    return snapshot;
  }

  /** Undo snapshot for a FREE-run resize: toggles the anchor (and the derived
   *  width property) before/after. */
  private createFreeResizeSnapshot(objectId: string, before: Coordinate[], after: Coordinate[]): Snapshot {
    let applyBefore = true;
    const snapshot: Snapshot = {
      restore: (): Snapshot => {
        try {
          const anchor = applyBefore ? before : after;
          getSketchObjectAnchorProperty(objectId).value = anchor.map((p) => ({ ...p }));
          getSketchObjectWidthProperty(objectId).value = Math.hypot(anchor[1].x - anchor[0].x, anchor[1].y - anchor[0].y);
        } catch {
          /* property service not ready */
        }
        applyBefore = !applyBefore;
        return snapshot;
      },
    };
    return snapshot;
  }
}

export type { WallEdgeHit };
